import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ListingPageTemplate from "@Pages/ListingPageTemplate/ListingPageTemplate";
import themeCreator from "@Themes/themeCreator";
import { useNavigate } from "react-router-dom";
import { getRestrictionTypeColorStyles } from "@Utils/Validations/HelperFunctions/StyleFunction";
import HistoryToggleOffRoundedIcon from "@mui/icons-material/HistoryToggleOffRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExitRequestLogs from "@Components/ExitRequestLogs/ExitRequestLogs";

function GenericExitRequestListing({
  title,
  PageIcon,
  iconColor,
  columns,
  renderActions,
  buttons = [],
  extraComponents = null,
  fetchPage,
  rows,
  pageLoading,
  rowCount,
  initialColumnVisibilityModel = {},
  filterComponent = null,
  exportApi = null,
}) {
  // const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const theme = themeCreator();
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedRequestMeta, setSelectedRequestMeta] = useState(null);

  const handleOpenLogs = (row) => {
    if (!row?.id) {
      return;
    }
    setSelectedRequestId(row.id);
    setSelectedRequestMeta({
      ingredientName: row?.ingredient?.name,
      casNumber: row?.ingredient?.casNumber,
      status: row?.status,
      userEmail: row?.user?.email,
    });
    setLogsModalOpen(true);
  };

  const handleCloseLogs = () => {
    setLogsModalOpen(false);
    setSelectedRequestId(null);
    setSelectedRequestMeta(null);
  };

  // Format response string for display based on numberOfRemainingResponses
  const formatResponse = (params) => {
    if (params.row.numberOfRemainingResponses === undefined) return "?/?";

    // Since we only have remaining responses, we show it directly
    return `${params.row.numberOfRemainingResponses} remaining`;
  };


  // Process columns to add custom opegetGridNumericOperatorsrators and formatters
  const processedColumns = columns.map((col) => {
    if (
      col.field === "response" ||
      col.field === "numberOfRemainingResponses"
    ) {
      return {
        ...col,
        valueGetter: (params) => formatResponse(params),
        renderCell:
          col.renderCell ||
          ((params) => {
            const responseText = formatResponse(params);
            const remainingResponses =
              params.row.numberOfRemainingResponses || 0;

            return (
              <span
                style={{
                  color: remainingResponses === 0 ? "green" : "grey",
                  fontStyle: remainingResponses === 0 ? "normal" : "italic",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {responseText}
              </span>
            );
          }),
      };
    }

    if (col.field === "restrictionType" && !col.renderCell) {
      return {
        ...col,
        renderCell: (params) => {
          const type = params.row.restrictionType;
          let displayText = type;

          switch (type) {
            case "BANNED":
              displayText = "Banned";
              break;
            case "RESTRICTED":
              displayText = "Restricted";
              break;
            case "LABELING_REQUIREMENTS":
              displayText = "Labeling Requirements";
              break;
            case "RESTRICTIONS_AND_LABELING":
              displayText = "Restrictions and Labeling";
              break;
            case "RESTRICTED_AND_SDS":
              displayText = "Restricted and SDS";
              break;
            default:
              displayText = type || "-";
          }

          const colorStyles = getRestrictionTypeColorStyles(type);

          return (
            <span
              style={{
                ...colorStyles,
                padding: "4px 8px",
                borderRadius: "16px",
                fontWeight: "bold",
                fontSize: "0.75rem",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {displayText}
            </span>
          );
        },
      };
    }

    if (col.field === "actions") {
      return {
        ...col,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {renderActions?.(params, navigate, theme)}
            <Tooltip title="View Logs">
              <IconButton
                color="primary"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenLogs(params.row);
                }}
              >
                <HistoryToggleOffRoundedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      };
    }

    // if (!col.filterOperators) {
    //   return {
    //     ...col,
    //   };
    // }

    return col;
  });


  const reqTableProps = {
    columns: processedColumns,
    disableRowSelectionOnClick: true,
    disableColumnMenu: true,
    initialColumnVisibilityModel,
    rowCount,
    rows,
    isLoading: pageLoading,
    fetchPage,
    minHeight: "100px", // Set minimum height for table
    // Null when the page has not opted in, in which case no export button renders.
    excelExport: exportApi
      ? { endpoint: exportApi, fileNamePrefix: "exit-requests" }
      : null,
  };

  //********************* RETURN *********************//
  return (
    <Box sx={{ p: 0 }}>
      <ListingPageTemplate
        title={title}
        PageIcon={PageIcon}
        iconColor={iconColor}
        buttons={buttons}
        tableProps={reqTableProps}
        serverPagination={true}
        filterComponent={filterComponent}
      />
      <Dialog
        open={logsModalOpen}
        onClose={handleCloseLogs}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            py: 2,
            px: 3,
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Exit Request Logs
            </Typography>
            {selectedRequestMeta?.ingredientName && (
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                {selectedRequestMeta.ingredientName}
                {selectedRequestMeta.casNumber
                  ? ` • CAS: ${selectedRequestMeta.casNumber}`
                  : ""}
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label="Close logs dialog"
            onClick={handleCloseLogs}
            sx={{
              color: theme.palette.common.white,
            }}
            size="small"
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            backgroundColor: theme.palette.grey[50],
            px: 3,
            py: 2,
          }}
        >
          {logsModalOpen && (
            <ExitRequestLogs
              exitRequestId={selectedRequestId}
              meta={selectedRequestMeta}
              height="60vh"
              minHeight="320px"
              variant="modal"
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: theme.palette.background.paper,
            px: 3,
            py: 2,
          }}
        >
          <Button onClick={handleCloseLogs} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {extraComponents}
    </Box>
  );
}

export default GenericExitRequestListing;
